/**
 * One triage row (the body of the prototype's `vRFQ` row template).
 *
 * Everything derived here is FE-owned per contract §3: the UOM conversion line, the
 * "qty × factor > on-hand" warning, the stock badge, the confidence bar. The server owns
 * whether a UOM is legal at all (422 UOM_NOT_IN_MASTER) — when it rejects one, the row
 * shows the master's real options from `error.body.available` instead of guessing.
 */

import { printedDescription, withoutInternalAnnotations } from '../../lib/internalNotes'
import { editedDescription } from '../../lib/rfq'
import { useEffect, useMemo, useState } from 'react'
import { Badge } from '../../components/Badge'
import { Button } from '../../components/ui'
import { Money } from '../../components/Money'
import { confidenceTone, exceedsOnHand, formatMoney, qtyInBaseUom, uomFactor } from '../../lib/format'
import { LinkCombo } from '../../components/LinkPicker'
import { mergeItemEnrichment, uomAvailabilityText, uomComboOptions } from '../../lib/rfq'
import { customerUomHint } from '../../lib/customerUom'
import { RequestUomLink } from './RequestUomLink'
import { RequestItemLink } from './RequestItemLink'
import { chosenContractFor } from '../../lib/blanketOrders'
import { IN_HOUSE_SUPPLIER } from '../../lib/directPricing'
import type { InHousePrice } from '../../lib/inHousePrice'
import { isUomConfirmed, unitChips, unitEquivalenceNote, type UomChoice } from '../../lib/uomChoice'
import { LineTextarea } from '../../components/LineTextarea'
import { getBaseCurrency } from '../../lib/api/baseCurrency'
import { recommendQty } from '../../lib/packSize'
import { type PriceSource } from '../../lib/api/useItems'
import type { Currency, EnquiryItem, Item, ItemContract, Money as MoneyValue, UomConversion } from '../../lib/types'
import { displayConfidence, type LineSuggestion } from './suggestions'

const CONFIDENCE_COLORS = { ok: 'var(--ok)', warn: 'var(--warn)', danger: 'var(--danger)' } as const

function ConfidenceBar({ value }: { value: number }) {
  /*
    Exactly 1 is never a similarity: the matcher scores below it, and 1 is what a hand pick
    from the catalogue or an exact code from the customer's sheet is stamped with. Said as
    what it is (Akhil, 2026-09-16 — "conf shows 100 when I click a different item").
  */
  if (value >= 1) {
    return (
      <span className="conf" title="Chosen by hand, or an exact code from the customer — not a similarity score">
        <span style={{ color: 'var(--ok)' }}>✓ chosen</span>
      </span>
    )
  }
  const tone = confidenceTone(value)
  const color = CONFIDENCE_COLORS[tone as keyof typeof CONFIDENCE_COLORS] ?? 'var(--muted)'
  return (
    <span className="conf">
      <span className="bar">
        <i style={{ width: `${Math.round(value * 100)}%`, background: color }} />
      </span>
      <span style={{ color }}>{Math.round(value * 100)}%</span>
    </span>
  )
}

export interface LineRowProps {
  line: EnquiryItem
  /** The enquiry this line belongs to — what an item/UOM request names as its reference (2026-09-19). */
  enquiryId: string
  item: Item | undefined
  /** Which price list `item.base_price` came from — see `useItemsByCode`. */
  priceSource?: PriceSource
  /**
   * Our own price for ONE of the line's unit, resolved from the `Item Price` rows — or `null`
   * when no row can reach that unit. See `lib/inHousePrice.ts` for why this is not
   * `item.base_price` scaled: the table holds a row per unit, and the master's single figure
   * carries no unit at all.
   */
  ourPrice?: InHousePrice | null
  /**
   * The standing contracts covering this item, from the orders the purchaser selected.
   *
   * Passed in rather than fetched. This row used to call `items.get_blanket_orders` for its own
   * item behind the `focused` latch — one request per row a purchaser touched, for an answer that
   * is empty for most of the catalogue. Blanket orders are now chosen once for the whole enquiry
   * (`BlanketOrderBar`), so every line is a Map lookup costing nothing.
   */
  blanketContracts?: ItemContract[]
  /** Refresh this line's item, prices and stock from the ERP (`erpSync.ts`, 2026-10-26). */
  onSyncFromErp?: () => void
  syncingFromErp?: boolean
  /**
   * Every ACTIVE contract covering this item, whether or not its order is selected.
   *
   * Only for showing a better deal that has not been taken — pricing still comes from
   * `blanketContracts`, so nothing here changes what a line is quoted at. A purchaser used to have
   * to tick contracts one at a time to discover which covered an item; this makes the comparison
   * the screen is for possible without that.
   */
  availableContracts?: ItemContract[]
  /**
   * Stock for every code on the table, loaded once by `RfqPage` alongside the item masters.
   *
   * Carries the shelf quantity AND what is reserved against it, not one derived number — see the
   * stock cell below. Absent for a code nothing has answered for yet, which renders "—".
   */
  stockByCode?: ReadonlyMap<string, { qty: number; reserved: number; available: number }>
  /**
   * Rate per candidate item code, for the match shortlist's price column.
   *
   * Batched ONCE for the whole table by `RfqPage` (one `Item Price` `in` query over every
   * candidate on every line), not per row — ten candidates across forty rows is four hundred
   * codes and still one request. Read-only here.
   */
  candidatePrices?: Map<string, { price: MoneyValue; source: PriceSource }>
  /** The item-master batch request is still in flight — `item` is not yet known. */
  itemPending: boolean
  selected: boolean
  selectable: boolean
  busy: boolean
  /** Set when the server rejected this line's last UOM change (422). */
  uomError: { message: string; available: UomConversion[] } | null
  /** `line.suppliers` holds `Supplier.code` — this resolves one to a display name for the chip.
   * Falls back to the code itself when the master hasn't loaded (or the supplier is unknown)
   * rather than blanking the chip. */
  supplierName: (code: string) => string
  /** What the AI proposed for this line that the purchaser has not acted on. `undefined` when
   * there is no extraction in the store — the row then renders exactly as it always did. */
  suggestion?: LineSuggestion
  /**
   * True only while "Accept all recommendations" is running. That loop applies a single snapshot
   * of `suggestions.byLine` taken at the start of the batch and never re-reads it mid-loop, so an
   * edit typed here while it runs would be silently clobbered when the loop reaches this row —
   * and this app has no undo. Freezing the qty/UOM inputs and the suggestion "use" buttons for
   * the batch's duration is what removes that window; it visibly explains why typing does
   * nothing rather than accepting input it is about to discard. Defaults to `false` so every
   * other caller (and every other row state) is unaffected.
   */
  disabled?: boolean
  onToggleSelect: () => void
  onOpenCatalog: () => void
  onOpenSuppliers: () => void
  onChangeQty: (qty: number) => void
  onChangeUom: (uom: string) => void
  /**
   * The unit is still the purchaser's to decide (`lib/uomChoice.ts`): the customer asked in a
   * unit this item is not sold in, or the line sits on a unit the item does not have. The row
   * says so, offers the item's units, and RFQ/quotation stay blocked until one is chosen.
   */
  uomChoice?: UomChoice | null
  /** The purchaser decided: keep or change the unit, and record the customer's. */
  onDecideUom: (uom: string) => void
  /** Scrolled to and flashed — the first undecided line when Send/Create was refused. */
  flash?: boolean
  /** The printed description — see `EnquiryItem.description`. Commits on blur. */
  onChangeDescription: (description: string) => void
  onChangeRoute: (route: 'contract' | 'rfq') => void
  onChangeContract: (contractId: string) => void
  onRemoveSupplier: (supplier: string) => void
  onDelete: () => void
  /** The sent RFQ this line is on, when a supplier is already pricing it — delete is disabled. */
  sentOnRfq?: string | null
  onUseItemCode?: (code: string) => void
  /** Records the filed item request on the line (`item_creation_req_ref`), 2026-09-19. */
  onItemRequested?: (requestName: string) => void
  /** Opens the item's quote history — see `ItemHistoryModal`. */
  onOpenHistory: () => void
  /**
   * Prices the line from our own stock at `ourPrice` — the same write the "Use our stock" button
   * in the history dialog makes, one click sooner: the in-house figure on the row IS the offer
   * (Akhil, 2026-09-17: "so they will directly be added as in-house stock price rather than
   * clicking on price this line"). Only offered while the row shows an in-house price.
   */
  onUseOurStock?: () => void
}

/**
 * Commits on blur, and only when changed — a description is typed in one go and a request per
 * keystroke would put the whole enquiry through `update_enquiry`'s wholesale write each time.
 */
function DescriptionField({
  value,
  edited,
  disabled,
  onCommit,
}: {
  value: string
  edited: boolean
  disabled: boolean
  onCommit: (description: string) => void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <div style={{ marginTop: 4 }}>
      {/* A textarea, grown to its lines (Akhil, 2026-09-15): a description is often several —
          size, spec, packing — and one line of input hid all but the first. */}
      <LineTextarea
        value={draft}
        disabled={disabled}
        title="Description as it will print on the RFQ and the quotation — edit freely; the customer's own text above is kept. Enter for a new line."
        placeholder="Description for the RFQ / quotation"
        style={{ fontSize: 12 }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const next = draft.trim()
          if (next !== '' && next !== value) onCommit(next)
          else if (next === '') setDraft(value)
        }}
      />
      <div style={{ color: 'var(--muted)', fontSize: 10.5 }}>{edited ? 'description · edited' : 'description · prints on RFQ & quotation'}</div>
    </div>
  )
}

export function LineRow({
  line,
  enquiryId,
  item: itemProp,
  priceSource,
  ourPrice,
  candidatePrices,
  blanketContracts,
  onSyncFromErp,
  syncingFromErp,
  availableContracts,
  stockByCode,
  itemPending,
  selected,
  selectable,
  busy,
  uomError,
  supplierName,
  suggestion,
  disabled = false,
  onToggleSelect,
  onOpenCatalog,
  onOpenSuppliers,
  onChangeQty,
  onChangeUom,
  uomChoice = null,
  onDecideUom,
  flash = false,
  onChangeDescription,
  onChangeRoute,
  onChangeContract,
  onRemoveSupplier,
  onDelete,
  sentOnRfq = null,
  onUseItemCode,
  onItemRequested,
  onOpenHistory,
  onUseOurStock,
}: LineRowProps) {
  const [qtyDraft, setQtyDraft] = useState(String(line.qty))
  useEffect(() => setQtyDraft(String(line.qty)), [line.qty])

  /*
    This row fetches NOTHING of its own any more.

    It used to fetch contracts and stock behind a `focused` latch, because doing it on render was
    an N+1 across a table that can carry sixty lines. Both are now resolved once for the whole
    table — contracts from the selected blanket orders, stock and item masters by `RfqPage` — so
    the figures are present when the table first appears instead of arriving after the purchaser
    clicks into a row, which is what made the column look like it was still thinking.
  */
  const itemCode = itemProp?.code ?? ''
  const ownStock = itemCode ? stockByCode?.get(itemCode) : undefined

  const item = useMemo(
    () =>
      itemProp
        ? mergeItemEnrichment(itemProp, {
            contracts: blanketContracts,
            stock: ownStock ? { stock_enabled: true, ...ownStock } : undefined,
          })
        : itemProp,
    [itemProp, blanketContracts, ownStock],
  )

  const baseUom = item?.base_uom ?? ''
  // The matched item's own units — what a unit request is judged against (`uomRequestProblem`).
  const itemUnits = useMemo(() => (item && line.item_code ? [item.base_uom, ...item.uom_conversions.map((c) => c.uom)].filter((u) => u !== '') : null), [item, line.item_code])
  /*
    Point 12 — the customer's own unit, off the PERSISTED line rather than the extraction result,
    so it is still shown the next time this enquiry is opened. See `lib/customerUom.ts`.
  */
  const customerHint = customerUomHint(line.raw_text, line.uom, line.customer_uom)
  // "customer asked in PCS — taken as Nos, the same unit" — the auto-select explained (Akhil, 2026-09-16).
  const equivalence = unitEquivalenceNote(line, item)
  const customerUom = customerHint?.uom ?? null
  /*
    Only a RECORDED unit that disagrees is a warning. An INFERRED one is read off the end of the
    customer's own text and is sometimes a product name rather than a unit ("TEA- BAG"), so it is
    presented as an observation — never a warning, never a block, never a write.
  */
  // A recorded confirmation (2026-09-18) settles the question: shown as decided, never as a warning.
  const uomConfirmed = isUomConfirmed(line)
  const customerUomUnresolved = customerHint?.source === 'recorded' && customerHint.differs && !uomConfirmed
  // The quantity the UOM options do their arithmetic with. Read from the DRAFT, not `line.qty`, so
  // typing 5 updates every option's "5 CASE = 60 TRAY" before the blur that saves it — the whole
  // reason the factor sits on the option is to be read while deciding.
  const qtyNumber = Math.max(0, Number(qtyDraft) || 0)
  // The matcher's own best score for this line, when it offered anything. `null` for a line it
  // was never asked about, or that it could not match at all.
  const topCandidateScore = suggestion?.candidates?.[0]?.score ?? null
  // The honest number for a MATCHED line: the matcher's own score when the line carries the
  // matcher's pick, the line's own value otherwise, `null` when there is no item at all.
  const matchedConfidence = displayConfidence(line, suggestion)
  // Recomputed from the customer's own words and the matched item's name — no request, no state.
  // `null` for any line where the two cannot be honestly compared.
  /*
    The line's own UOM is passed, and it is load-bearing: a line COUNTED in packets makes any
    weight in the customer's text a description of the packet, not a total to order. Without it
    this offered "set qty to 1" on a line asking for 100 PKT — see `recommendQty`.
  */
  // The customer's words without our notes — what the pack hint reads and what a "request this
  // item" is prefilled with. `customerUomHint` above keeps the raw record: the unit note IS its input.
  const wording = withoutInternalAnnotations(line.raw_text)
  const packHint = useMemo(
    () => (item ? recommendQty(wording, item.name, line.uom) : null),
    [item, wording, line.uom],
  )
  const factor = item ? uomFactor(item, line.uom) : null
  const inMaster = factor !== null
  const baseQty = item ? qtyInBaseUom(item, line.qty, line.uom) : null
  // Undefined until `items.get_stock` actually resolves `stock_enabled: true` — see
  // `mergeItemEnrichment`. Not a zero: `exceedsOnHand` returns false for it and the stock cell
  // shows "—" rather than a red `nil`.
  const onHand = item?.on_hand
  // A contract-priced line is fulfilled by the supplier, so on-hand stock doesn't gate it.
  const overStock = item && line.route !== 'contract' ? exceedsOnHand(item, line.qty, line.uom) : false
  const needQty = baseQty ?? line.qty
  const contracts = item?.contracts ?? []
  /*
    The cheapest contract this line is NOT already priced from — `buildRateIndex` sorts cheapest
    first, so the head of the list is the best available. Shown only when it actually beats what
    the line has, so a row already on the best rate says nothing.
  */
  /*
    EVERY contract covering this item — the ticked ones and the rest, in one list.

    Showing only the ticked orders meant a purchaser had to go and tick a contract before they
    could see what it charged, which is backwards: the point of the screen is to compare them and
    then decide. Merged and sorted cheapest first (`buildRateIndex`'s own order), deduplicated on
    the order name, with the unticked ones marked so it stays clear which are in force.
  */
  const allContracts = useMemo(() => {
    const byId = new Map<string, { contract: ItemContract; selected: boolean }>()
    for (const c of contracts) byId.set(c.id, { contract: c, selected: true })
    for (const c of availableContracts ?? []) if (!byId.has(c.id)) byId.set(c.id, { contract: c, selected: false })
    return [...byId.values()].sort((a, b) => a.contract.rate.amount - b.contract.rate.amount)
  }, [contracts, availableContracts])
  /*
    Matched on the rate and supplier that PERSIST, not on `contract_id`, which the wire drops.
    Reading the id alone made the dropdown snap back to the first contract after a reload while
    the line was really priced from the second — see `chosenContractFor`.
  */
  const chosenContract =
    chosenContractFor(contracts, line) ??
    // Priced from an order the purchaser has NOT ticked (the row's own list allows it): matched
    // exactly, on rate and supplier, never by the cheapest-first fallback — that would label a
    // line priced by hand as if a contract had priced it.
    allContracts
      .map((c) => c.contract)
      .find((c) => line.route === 'contract' && line.suppliers.length === 1 && c.supplier === line.suppliers[0] && c.rate.amount === line.selected_rate)

  /**
   * Three distinct states, not two: a line with no `item_code` is genuinely unmatched, but
   * a matched line whose master record is still loading must NOT be painted red — that
   * would tell the purchaser their matched lines need attention when they don't.
   */
  const matchState: 'unmatched' | 'loading' | 'loaded' = item
    ? 'loaded'
    : line.item_code
      ? 'loading'
      : 'unmatched'
  const unknownItem = matchState !== 'loaded'

  /**
   * Every catalogue-picker entry point writes `item_code` — exactly what a stale `applyingAll`
   * loop snapshot writes, and exactly the field the loop's own last write could clobber (or be
   * clobbered by) if the picker landed mid-batch. Frozen alongside the qty/UOM inputs and the
   * suggestion "use" buttons for the same reason: nothing on the table should change out from
   * under the batch while it runs.
   */
  const openCatalog = disabled ? undefined : onOpenCatalog

  return (
    <>
      <tr
        id={`line-${line.line_id}`}
        style={{
          ...(busy ? { opacity: 0.55 } : {}),
          ...(flash ? { outline: '2px solid var(--warn)', outlineOffset: -2 } : {}),
        }}
      >
        <td>
          {selectable ? (
            <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={`Select ${line.line_id}`} />
          ) : null}
        </td>

        {/* The customer's own serial number — read-only, echoed exactly, carried to the quotation. */}
        <td style={{ textAlign: 'center' }}>
          <Badge
            tone="muted"
            className="mono"
            title="Customer's serial number exactly as sent in the mail / image — read-only"
          >
            {line.cust_sno || '—'}
          </Badge>
        </td>

        <td>
          {/* The customer's own words, WITHOUT our unit annotation — that is shown as its own
              field below rather than trailing the description in parentheses. */}
          <div style={{ fontWeight: 600 }}>{withoutInternalAnnotations(line.raw_text)}</div>
          <div style={{ color: 'var(--muted)', fontSize: 11 }}>from customer list</div>
          {/*
            The description that PRINTS — on the RFQ to suppliers and on the customer quotation.
            Starts as the customer's text and is editable here (Akhil, 2026-09-14) so a
            purchaser can tidy "GENSET 200KVA W/ BOARDS" into something a supplier can price
            without the customer's own line being rewritten: `raw_text` above stays as sent.
          */}
          <DescriptionField
            value={printedDescription(line)}
            edited={editedDescription(line) !== ''}
            disabled={disabled}
            onCommit={onChangeDescription}
          />

          {/*
            Point 12 — what unit the CUSTOMER asked in.

            Read off the persisted line, so it is still here on the next visit; it used to come
            from the in-memory extraction and vanished on reload. Present only when the UOM master
            could not verify what they wrote, which is precisely when the purchaser has to choose a
            unit and needs to know what they are translating from.

            Warn-toned while the line is still on a different unit, muted once they agree — the
            colour is the difference between "you have something to resolve" and "resolved, here
            is the record".
          */}
          {customerHint ? (
            <div
              style={{
                fontSize: 11,
                marginTop: 3,
                color: customerUomUnresolved ? 'var(--warn)' : 'var(--muted)',
              }}
              title={
                customerHint.source === 'recorded'
                  ? customerHint.differs
                    ? `The customer asked in ${customerHint.uom}, which the UOM master does not accept. Pick the matching unit for this item.`
                    : `The customer asked in ${customerHint.uom}, and this line is on it.`
                  : `Read from the customer's own wording. This line is on ${line.uom}.`
              }
            >
              {customerUomUnresolved ? '⚠ ' : customerHint.differs ? '· ' : '✓ '}
              {/* "asked in" for a recorded unit, "wrote" for one we read out of their text — the
                  wording carries the confidence, so a hint is never mistaken for a fact. */}
              customer {customerHint.source === 'recorded' ? 'asked in' : 'wrote'}{' '}
              <b>{customerHint.uom}</b>
              {uomConfirmed && customerHint.differs ? (
                <span> — quoted per <b>{line.uom}</b>, confirmed</span>
              ) : equivalence ? (
                <span style={{ color: 'var(--warn)' }}> — taken as <b>{line.uom}</b>, the same unit</span>
              ) : customerHint.differs ? (
                <span style={{ opacity: 0.75 }}> · line is {line.uom}</span>
              ) : null}
              {/*
                POINT 14 LIVES HERE, and this is the only place it is reachable.

                It was originally next to the "not in master" warning further down, which needs a
                RESOLVED item whose conversions reject the line's unit. Measured against three real
                enquiries: that combination essentially never occurs, because a rejected unit has
                already been forced to the `Nos` placeholder — and `Nos` is in almost every item's
                conversions. The link rendered nowhere.

                A `recorded` customer unit IS the master having rejected it: that is the only
                circumstance in which the annotation gets written. It needs no matched item, and it
                fires on exactly the four lines per enquiry that have this problem.
              */}
              {customerUomUnresolved ? (
                <>
                  {' · '}
                  <RequestUomLink uom={customerHint.uom} enquiryId={enquiryId} rowId={line.cust_sno} itemCode={line.item_code} itemUnits={itemUnits} disabled={disabled} />
                </>
              ) : null}
            </div>
          ) : null}
        </td>

        <td>
          {matchState === 'loaded' && item ? (
            <>
              <div
                className="linkish"
                style={{ border: 0, fontWeight: 700, cursor: disabled ? 'not-allowed' : undefined }}
                onClick={openCatalog}
              >
                {item.name}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                {item.code} · {item.group}
                {/*
                  A weak match stays marked AFTER it is applied, which is the whole point of the
                  flag. `searchMatch.ts` now proposes the matcher's top hit whatever it scored, so
                  "Accept all" can sweep in a 0.11 guess — and once applied it renders in exactly
                  the same matched-and-done style as a 0.92 certainty unless something says
                  otherwise. Shown only while the applied code IS the flagged one: change the item
                  by hand and the warning has nothing left to describe.
                */}
                {suggestion?.matchLowConfidence && suggestion.itemCode === item.code ? (
                  <>
                    {' · '}
                    <span style={{ color: 'var(--warn)' }} title="Matched automatically on a low similarity score — worth checking against the customer's text">
                      low confidence
                    </span>
                  </>
                ) : null}
              </div>
              <div style={{ fontSize: 11, marginTop: 2 }}>
                {/*
                  Labelled, because only ~10 items in the catalogue have a real in-house (buying)
                  price — the rest fall back to a selling price, and a selling price read as a cost
                  would wreck a margin decision. `priceSource` says which list it came from.
                */}
                {/*
                  Our own price for ONE of the LINE's unit (Akhil, 2026-09-14: "after changing the
                  uom the in-house stock remains the same"). Read from the `Item Price` row for
                  that unit — 1707050028 is 1.00/PKT and 40.00/CTN on the same list — or derived
                  through the master when no row exists for it, and labelled either way. Falls
                  back to the master's unlabelled figure only when the table has no rows at all.
                */}
                {ourPrice ? (
                  /*
                    Clickable when a handler is given: one click prices the line from our own
                    stock at this figure, no dialog. Same source and same label as before —
                    the `title` gains the action, and says what the click commits to.
                  */
                  <span
                    role={onUseOurStock && !disabled ? 'button' : undefined}
                    tabIndex={onUseOurStock && !disabled ? 0 : undefined}
                    data-testid="use-our-stock"
                    onClick={onUseOurStock && !disabled ? onUseOurStock : undefined}
                    onKeyDown={
                      onUseOurStock && !disabled
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onUseOurStock()
                            }
                          }
                        : undefined
                    }
                    style={onUseOurStock && !disabled ? { cursor: 'pointer' } : undefined}
                  >
                    <Badge
                      tone={ourPrice.buying ? 'muted' : 'warn'}
                      title={`${ourPrice.buying ? 'In-house cost' : 'SELLING price, not a cost'} — ${ourPrice.price_list}${
                        ourPrice.derived ? `. No row for ${ourPrice.uom}: derived from ${ourPrice.from.rate}/${ourPrice.from.uom} through the unit master` : ''
                      }${onUseOurStock && !disabled ? `. Click to price this line from our own stock at ${ourPrice.amount.toFixed(2)}/${ourPrice.uom} — no RFQ.` : ''}`}
                    >
                      <Money value={{ amount: ourPrice.amount, currency: ourPrice.currency as Currency }} />/{ourPrice.uom}
                      {ourPrice.derived ? ' ≈' : null}
                      {!ourPrice.buying ? ' sell' : null}
                      {onUseOurStock && !disabled ? ' · use' : null}
                    </Badge>
                  </span>
                ) : item.base_price ? (
                  <Badge
                    tone={priceSource?.buying ? 'muted' : 'warn'}
                    title={`${
                      priceSource ? `${priceSource.buying ? 'In-house cost' : 'SELLING price, not a cost'} — ${priceSource.price_list}. ` : ''
                    }The catalogue's single figure — it carries no unit, so the base unit is assumed.`}
                  >
                    <Money value={item.base_price} />/{item.base_uom}
                    {priceSource && !priceSource.buying ? ' sell' : null}
                  </Badge>
                ) : null}{' '}
                <span
                  className="linkish"
                  style={disabled ? { cursor: 'not-allowed' } : undefined}
                  onClick={openCatalog}
                >
                  change item
                </span>
                {/*
                  TWO links, not four.

                  This grew to "change item · price · quote history · use our stock", which wrapped
                  onto three lines inside a narrow column and buried the item name it sits under.
                  The last three all open the SAME dialog, so they are one control — named for what
                  it does rather than for one of the things it shows.
                */}
                <span style={{ color: 'var(--line)', margin: '0 6px' }}>·</span>
                <span
                  className="linkish"
                  onClick={onOpenHistory}
                  title="Past supplier quotes for this item, and price the line from one — or from our own stock. No RFQ either way."
                >
                  price this line
                </span>
              </div>
              {/*
                Offered on ANY match below 100% (Akhil, 2026-09-14) — not only on an unmatched
                line. The matcher always answers something, so a genuinely absent item arrives
                here as a 3% "match", and the request that would fix it was hidden behind the
                very state it prevents. At exactly 100% the line was matched by a person or the
                mail reader, and the request is one control too many.
              */}
              {line.confidence < 1 ? (
                <div style={{ marginTop: 2 }}>
                  <RequestItemLink description={wording} uom={line.uom} enquiryId={enquiryId} rowId={line.cust_sno} existingRequest={line.item_creation_req_ref} onRequested={onItemRequested} disabled={disabled} />
                </div>
              ) : null}
            </>
          ) : matchState === 'loading' ? (
            /*
              Two states share this branch and they are NOT the same thing:

                itemPending   the master is still being fetched — wait, do not offer a fix
                otherwise     the catalogue has no such item, and the line is STUCK

              The second used to render as dead text. Nothing on it was clickable, `AutoMatcher`
              skips it (a line with an `item_code` is treated as already decided, deliberately —
              it must never overwrite a purchaser's choice), and the value shown is whatever is in
              `item_code`, which in the failing case is not a code at all but a description the
              extraction wrote there. So the row displayed an item nobody could act on, with no
              way to change it. It opens the picker now, like every other unmatched state.

              The value is quoted and labelled rather than printed bare: a sentence of English
              presented where a code belongs reads as a matched item, which is the opposite of
              what this state means.
            */
            itemPending ? (
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                <span className="mono">{line.item_code}</span>
                <div style={{ fontSize: 11 }}>loading item master…</div>
              </div>
            ) : (
              <>
                <Badge tone="danger" className="linkish" title="The catalogue has no item with this code — pick the real one">
                  <span style={disabled ? { cursor: 'not-allowed' } : undefined} onClick={openCatalog}>
                    not in catalogue — click to match →
                  </span>
                </Badge>
                <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 3 }}>
                  saved as <span className="mono">“{line.item_code}”</span>
                </div>
                {/* Point 14's item half. Offered beside "click to match" because matching is the
                    right answer when the item exists under another name, and this is the right one
                    when it does not exist at all. */}
                <RequestItemLink description={wording || line.item_code || ''} uom={line.uom} enquiryId={enquiryId} rowId={line.cust_sno} existingRequest={line.item_creation_req_ref} onRequested={onItemRequested} disabled={disabled} />
              </>
            )
          ) : (
            <>
              <Badge tone="danger" className="linkish" title="Pick the master item this line refers to">
                <span style={disabled ? { cursor: 'not-allowed' } : undefined} onClick={openCatalog}>
                  unmatched — click to match →
                </span>
              </Badge>
              {/* The same escape as the not-in-catalogue state above: matching is right when the
                  item exists under another name, requesting is right when it does not exist. */}
              <RequestItemLink description={wording} uom={line.uom} enquiryId={enquiryId} rowId={line.cust_sno} existingRequest={line.item_creation_req_ref} onRequested={onItemRequested} disabled={disabled} />

              {/*
                The shortlist from `bosun-search` — the whole reason that service exists. Without
                it an unmatched line offers a modal and an empty search box for text the customer
                already wrote; with it, the common case is one click on the row.

                Every entry is applied directly (unlike `suggestion.itemCode` below, which may be
                a code the catalogue rejected): these came back FROM the catalogue, so none can be
                an unknown Link. The score is shown because a 0.91 and a 0.42 deserve different
                amounts of trust, and `null` prints nothing rather than a fabricated number.
              */}
              {(suggestion?.candidates?.length ?? 0) > 0 ? (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  {/*
                    ONLY the default — the matcher's top-ranked answer.

                    The shortlist used to render all ten here, which put ten rows of alternatives
                    under every unmatched line and made a forty-line enquiry unreadable. The
                    alternatives did not stop being useful; they belong where a choice is actually
                    being made. `CatalogPickerModal` (opened by clicking the item) pins the same
                    ranked list at the top of its results WITH rate and stock on each row, so
                    comparing options happens in one place that has room for it.

                    ---- When this is even reachable now ----

                    `AutoMatcher` applies this same top hit without a click, so most lines are
                    matched before anyone reads them and this block never renders for them. It
                    remains the manual path for the cases auto-match deliberately leaves alone: a
                    line whose write FAILED, and — the real one — a line whose item a purchaser
                    CLEARED. Auto-match never re-applies to a line it has already tried, so
                    clearing an item must leave a way to put one back without opening the picker.

                    Rate and stock stay on this row for that reason: it is the one option a
                    purchaser accepts without opening anything.
                  */}
                  {(() => {
                    const best = suggestion!.candidates[0]
                    const priced = candidatePrices?.get(best.item_code)
                    const stock = stockByCode?.get(best.item_code)
                    const others = suggestion!.candidates.length - 1
                    return (
                      <>
                        <span style={{ color: 'var(--muted)' }}>best match: </span>
                        <button
                          className="linkish"
                          disabled={disabled}
                          title={`Match this line to ${best.item_code}`}
                          onClick={() => onUseItemCode?.(best.item_code)}
                          style={{ fontWeight: 700 }}
                        >
                          {best.item_name || best.item_code}
                        </button>
                        {best.score !== null ? (
                          <span style={{ color: 'var(--muted)' }}> · {Math.round(best.score * 100)}%</span>
                        ) : null}
                        {/*
                          The price carries its LIST with it: only ~10 items in the catalogue have
                          a real in-house cost, so most of these are SELLING prices, and a selling
                          price read as a cost wrecks a margin decision. Never a bare number.
                        */}
                        {priced ? (
                          <span
                            style={{ color: priced.source.buying ? 'var(--muted)' : 'var(--warn)' }}
                            title={`${priced.source.buying ? 'In-house cost' : 'SELLING price, not a cost'} — ${priced.source.price_list}`}
                          >
                            {' · '}
                            {formatMoney(priced.price)}
                            {priced.source.buying ? '' : ' sell'}
                          </span>
                        ) : null}
                        {/*
                          Absent, not zero, when we did not ask or could not be told — `0 in stock`
                          is a claim, and on this bench `items.get_stock` cannot make it for any
                          item (no ERP configured in Bosun Settings). See `useCandidateStock`.
                        */}
                        {/* Both figures the API gives — on the shelf, and how much of it is
                            already promised. See `StockCell` on why the subtraction alone
                            misleads. */}
                        {stock !== undefined ? (
                          <span
                            style={{ color: stock.available > 0 ? 'var(--ok)' : 'var(--danger)' }}
                            title={`${stock.qty} on the shelf · ${stock.reserved} reserved · ${stock.available} available`}
                          >
                            {' · '}
                            {stock.qty} in stock
                            {stock.reserved > 0 ? `, ${stock.reserved} reserved` : ''}
                          </span>
                        ) : null}
                        {/* Names how many alternatives exist and where they are — without it the
                            single row reads as the only thing the matcher found. */}
                        {others > 0 ? (
                          <div style={{ marginTop: 2 }}>
                            <button
                              className="linkish"
                              style={{ color: 'var(--muted)' }}
                              disabled={disabled}
                              onClick={openCatalog}
                            >
                              {others} other match{others === 1 ? '' : 'es'} — compare rate &amp; stock
                            </button>
                          </div>
                        ) : null}
                      </>
                    )
                  })()}
                </div>
              ) : null}
              {suggestion?.itemCode && !suggestion.itemCodeRejected ? (
                <div style={{ fontSize: 11, marginTop: 3 }}>
                  <span style={{ color: 'var(--muted)' }}>AI: </span>
                  <button className="linkish" disabled={disabled} onClick={() => onUseItemCode?.(suggestion.itemCode!)}>
                    {suggestion.itemCode} · use
                  </button>
                </div>
              ) : suggestion?.itemCode && suggestion.itemCodeRejected ? (
                /* The catalogue does not have this code — `verifyItemCodes` proved it. Applying it
                   in one click would put an invalid Link on the line and fail the WHOLE document
                   on save, not just this row. So it opens the picker instead of assigning. */
                <div style={{ fontSize: 11, marginTop: 3, color: 'var(--warn)' }}>
                  AI read <b>{suggestion.itemCode}</b> —{' '}
                  <button className="linkish" onClick={onOpenCatalog}>
                    search the catalogue
                  </button>
                </div>
              ) : null}
            </>
          )}
        </td>

        <td>
          {/* A line the reader brought through with no figure (2026-09-22) asks for it: the
              send waits until every RFQ line has one. */}
          <input
            type="number"
            value={qtyDraft}
            style={{ width: 60, ...(!(line.qty > 0) ? { borderColor: 'var(--warn)', background: 'color-mix(in srgb, var(--warn) 10%, var(--panel))' } : {}) }}
            title={!(line.qty > 0) ? 'No quantity was read for this line — enter the figure from the customer’s list' : undefined}
            aria-invalid={!(line.qty > 0)}
            disabled={disabled}
            onChange={(e) => setQtyDraft(e.target.value)}
            onBlur={() => {
              const next = Math.max(0, Number(qtyDraft) || 0)
              if (next !== line.qty) onChangeQty(next)
            }}
          />{' '}
          {/*
            A combo, not a `<select>`, so every unit can carry its own conversion — see
            `uomComboOptions`. A native option renders one line of text and nothing else, which is
            why reading a factor used to cost one selection per unit.

            `useOptions` calls no hooks, so defining it here is safe: `LinkCombo` invokes it
            exactly once per render (its own header says so), and it closes over the item and the
            live quantity, which is what lets an option say `2 CASE = 24 TRAY` rather than a rate.
          */}
          <span style={{ display: 'inline-block', width: 96, verticalAlign: 'top' }}>
            <LinkCombo
              // EMPTY while the unit is undecided (Akhil, 2026-09-16): the stored value is only
              // there because the field is mandatory, and showing it reads as a choice made.
              value={uomChoice ? '' : line.uom}
              disabled={disabled}
              placeholder={uomChoice ? 'choose unit…' : 'unit…'}
              onChange={uomChoice ? onDecideUom : onChangeUom}
              useOptions={(search) => {
                const term = search.trim().toLowerCase()
                // The line's stored UOM is kept in the list even when the master doesn't define
                // it, so the control shows what is actually saved rather than appearing to have
                // silently snapped to another unit. Same guarantee the old `<select>` gave.
                // Point 12 — the customer's unit is passed in so it can be marked and floated
                // to the top where the item's master defines it. See `uomComboOptions`.
                /*
                  ONLY the item's own units (Akhil, 2026-09-16). This used to append the line's
                  current unit marked "not in master" so the control showed what was saved — but
                  offering it lets a purchaser re-select a unit the item is not sold in, which is
                  the one outcome the empty picker exists to prevent. The saved value is still
                  visible in the warning beneath.
                */
                const options = uomComboOptions(item, qtyNumber, customerUom)
                return {
                  data: options.filter((o) => term === '' || o.name.toLowerCase().includes(term)),
                  isPending: false,
                }
              }}
            />
          </span>

          {/*
            THE UNIT IS UNDECIDED (Akhil, 2026-09-15). A stored unit exists only because the field
            is mandatory; it is not a decision. Said in warning tone, with the item's units as one
            click each, "keep" for the one it sits on, and the request for the customer's own.
            Nothing goes to a supplier or a customer from this line until one of these is pressed.
          */}
          {uomChoice && item ? (
            <div className="warnflag" style={{ marginTop: 4, whiteSpace: 'normal' }}>
              ⚠ Choose the unit — {uomChoice.reason}.
              <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* The customer's own unit leads, named as theirs — see `unitChips`. */}
                {unitChips(item, line.uom, uomChoice.customerUnit).map(({ uom: u, label, customers }) => (
                  <button
                    key={u}
                    type="button"
                    className="linkish"
                    disabled={disabled}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onDecideUom(u)}
                    style={customers ? { fontWeight: 700 } : undefined}
                    title={
                      u === line.uom
                        ? `Keep ${u} and record that the customer asked in ${uomChoice.customerUnit}`
                        : customers
                          ? `Quote per ${u} — the unit the customer asked in`
                          : `Quote per ${u}`
                    }
                  >
                    {label}
                  </button>
                ))}
                {/* Only when there IS a customer unit to request — the line's own unit is already a UOM. */}
                {uomChoice.customerUnit ? (
                  <>
                    <span style={{ color: 'var(--line)' }}>·</span>
                    <RequestUomLink uom={uomChoice.customerUnit} enquiryId={enquiryId} rowId={line.cust_sno} itemCode={line.item_code} itemUnits={itemUnits} disabled={disabled} />
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {/*
            "The customer asked for 3 KG, this comes in 1.5 KG — order 2."

            Shown, never applied on its own: this changes HOW MUCH gets ordered, and the pack size
            is read out of the item's name (`packSize.ts`), which is a parse, not a field. The
            arithmetic is on screen so the purchaser can check it in the second it takes, rather
            than trust a number that appeared by itself. Silent whenever either side is
            unparseable or the two are in different families — most lines show nothing.
          */}
          {packHint && packHint.qty !== qtyNumber ? (
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--brand)' }}>
              ✦ {packHint.explanation}{' '}
              <button
                type="button"
                className="linkish"
                disabled={disabled}
                /*
                  Keeps focus in the qty input, which is not cosmetic: that input COMMITS ON BLUR,
                  so clicking this while a half-typed value sits in it fired two writes — the
                  blur's, then this one — for a single click. Preventing the default mousedown
                  stops the blur, so the press means exactly one thing.
                */
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onChangeQty(packHint.qty)}
              >
                set qty to {packHint.qty}
              </button>
            </div>
          ) : null}

          {/*
            The in-memory "customer said X" hint that used to sit here is GONE — superseded by the
            persisted one in the FROM CUSTOMER cell (`customerUomOf`). That one reads off the saved
            line, so it is still there on the next visit; this one came from the extraction result
            and disappeared on reload. Saying it twice while one of the two was unreliable was
            worse than saying it once, well.
          */}

          {matchState === 'loaded' && item && line.uom !== baseUom ? (
            inMaster ? (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--muted)' }}>
                1 {line.uom} = <b style={{ color: 'var(--ink)' }}>{factor}</b> {baseUom}{' '}
                <Badge tone="ok">from master</Badge> → <b style={{ color: 'var(--brand)' }}>
                  {baseQty} {baseUom}
                </b>
              </div>
            ) : (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--warn)' }}>
                ⚠ “{line.uom}” not in master · available: {uomAvailabilityText(item)}{' '}
                <span className="linkish" onClick={() => onChangeUom(baseUom)}>
                  use {baseUom}
                </span>{' '}
                {/*
                  Point 14. The base unit is the right escape when it will do; this is the one for
                  when the customer genuinely orders in a unit the master has never heard of, and
                  quoting in something else would be answering a different question.
                */}
                <RequestUomLink uom={line.uom} enquiryId={enquiryId} rowId={line.cust_sno} itemCode={line.item_code} itemUnits={itemUnits} disabled={disabled} />
              </div>
            )
          ) : null}

          {/* The server's own verdict, when it rejected the last change. */}
          {uomError ? (
            <div className="warnflag">
              ⚠ {uomError.message} · master allows{' '}
              {uomError.available.map((a) => `${a.uom}${a.factor === 1 ? ' (base)' : `=${a.factor}`}`).join(', ')}
            </div>
          ) : null}

          {overStock ? (
            <div style={{ marginTop: 3, fontSize: 10.5, color: 'var(--danger)', fontWeight: 700 }}>
              {/* `overStock` is only ever true when `onHand` is a real number. */}⚠ {needQty} {baseUom} exceeds
              available {onHand}
            </div>
          ) : null}
        </td>

        <td>
          {unknownItem ? (
            ''
          ) : allContracts.length ? (
            /*
              Gated on EVERY contract covering the item, not the ticked ones (Akhil, 2026-09-16):
              a line moved off its contract dropped out of the bar's selection, and with it the
              route control and the list — "not detecting" — leaving no way to put it back.
            */
            <>
              <select value={line.route} onChange={(e) => onChangeRoute(e.target.value as 'contract' | 'rfq')}>
                <option value="contract">Provision · contract</option>
                <option value="rfq">Send RFQ</option>
              </select>
              {allContracts.length > 0 ? (
                /*
                  Every contracted rate ON THE ROW, not hidden behind a dropdown.

                  A `<select>` showed one rate and made the purchaser open it to discover there
                  were others, let alone what they cost — so the comparison this feature exists
                  for was invisible at a glance. Listed instead: cheapest first, the one in force
                  marked, each a single click to switch. Six suppliers on this bench hold two or
                  three overlapping contracts, so a line carrying several rates is normal.

                  Shown for ANY route, not only a contract-routed line: an RFQ line that a contract
                  covers is exactly the one worth knowing about, and clicking moves it across.
                */
                <div style={{ marginTop: 3, fontSize: 11 }}>
                  {allContracts.map(({ contract: c, selected }, index) => {
                    const inForce = line.route === 'contract' && c.id === chosenContract?.id
                    return (
                      <div key={`${c.id}-${c.supplier}`} style={{ marginTop: index === 0 ? 0 : 2 }}>
                        <button
                          type="button"
                          className="linkish"
                          disabled={disabled || inForce}
                          title={
                            inForce
                              ? 'Priced from this contract'
                              : `Price this line from ${c.supplier} at ${formatMoney(c.rate)}`
                          }
                          onClick={() => onChangeContract(c.id)}
                          style={{
                            fontWeight: inForce ? 700 : 400,
                            color: inForce ? 'var(--violet)' : selected ? undefined : 'var(--warn)',
                          }}
                        >
                          {inForce ? '● ' : '○ '}
                          {c.supplier} @ {formatMoney(c.rate)}
                        </button>
                        {/* Named, because cheapest is what the row defaults to and a purchaser
                            overriding it should see what they are giving up. */}
                        {index === 0 && allContracts.length > 1 ? (
                          <span style={{ color: 'var(--muted)' }}> · cheapest</span>
                        ) : null}
                        {/* An order the purchaser has not ticked. Pickable anyway — the tick is
                            about applying a contract to the whole enquiry, not about whether one
                            line may use it. */}
                        {!selected ? <span style={{ color: 'var(--muted)' }}> · not selected</span> : null}
                      </div>
                    )
                  })}
                  <div style={{ color: 'var(--muted)', fontSize: 10.5, marginTop: 2 }}>
                    {allContracts.length} blanket order{allContracts.length === 1 ? '' : 's'} cover this item
                  </div>
                </div>
                  ) : line.route === 'contract' && chosenContract ? (
                <div style={{ color: 'var(--violet)', fontSize: 11, marginTop: 3 }}>
                  {chosenContract.id} · {chosenContract.supplier} @ <Money value={chosenContract.rate} />
                </div>
              ) : null}
            </>
          ) : line.route === 'contract' ? (
            /*
              Priced, but NOT from a blanket order — from a supplier's past quote, or from our own
              shelf (`lib/directPricing.ts`).

              This cell used to render the route control only when the item had blanket contracts,
              and fell through to a hardcoded "Needs RFQ" badge otherwise. So a line the purchaser
              had just priced from history showed "Needs RFQ" while the suppliers column beside it
              said "fixed by contract" — the two cells contradicting each other, and the pricing
              looking as though it had not happened at all.

              The route belongs to the LINE, not to whether a blanket order exists for the item.
            */
            <>
              <select value={line.route} onChange={(e) => onChangeRoute(e.target.value as 'contract' | 'rfq')}>
                <option value="contract">Priced · no RFQ</option>
                <option value="rfq">Send RFQ</option>
              </select>
              <div style={{ color: 'var(--violet)', fontSize: 11, marginTop: 3 }}>
                {line.suppliers[0] ?? 'priced'}
                {/* The company's own currency — a contracted or historic rate is recorded as a
                    bare number on this wire, with the currency assumed (CLAUDE.md). */}
                {line.selected_rate > 0
                  ? ` @ ${formatMoney({ amount: line.selected_rate, currency: getBaseCurrency() })}`
                  : ''}
              </div>
            </>
          ) : (
            <Badge tone="info">Needs RFQ</Badge>
          )}
        </td>

        <td>
          {/*
            Confidence in the ITEM, which is the only thing this column can honestly be about.

            An unmatched line has no item to be confident in, so it shows nothing — it used to
            show 100%, because the mail reader stamps `confidence: 1` on every line it parses
            (`useMailExtraction.ts`). That number means "the text was read", and printing it as a
            green full bar beside "unmatched — click to match" and three guesses at 21%, 15% and
            8% told the purchaser the exact opposite of the truth.

            When the matcher offered candidates, its own top score is shown instead: a real
            measurement of the thing the column claims to measure.

            That rule now applies to MATCHED lines too, via `displayConfidence`. `AutoMatcher`
            sets items from the matcher, so a line matched at 0.03 was rendering a full green 100%
            bar beside the words "low confidence" — the column contradicting the label next to it.
          */}
          {matchedConfidence !== null ? (
            <ConfidenceBar value={matchedConfidence} />
          ) : topCandidateScore !== null ? (
            <span title="Best catalogue match — not yet applied to this line">
              <ConfidenceBar value={topCandidateScore} />
            </span>
          ) : (
            <span style={{ color: 'var(--muted)' }} title="No item matched yet — nothing to be confident about">
              —
            </span>
          )}
        </td>

        <td>
          {matchState === 'loaded' && item ? (
            <>
              {/* `on_hand` is optional: stock is unknown until `items.get_stock` answers for this
                  item. "—" says that; a zero would claim the item is out of stock and earn a red
                  `nil` badge it has not earned.

                  The number shown is AVAILABLE — on the shelf minus what is already committed to
                  other orders. See `mergeItemEnrichment`: 28 on the shelf against 33 reserved is
                  not 28 to sell, and this column is what a purchaser decides on. The shelf figure
                  is on the hover, not on the row, because it is context rather than the answer. */}
              {onHand === undefined ? (
                <span className="mono" style={{ color: 'var(--muted)' }}>
                  —
                </span>
              ) : (
                <>
                  {/*
                    BOTH figures, never one subtracted from the other on screen.

                    A single derived number was actively confusing: an item with 28 on the shelf
                    and 33 already promised rendered as "−5", which reads as a data error rather
                    than as a fact about the warehouse. What a purchaser needs is the two things
                    the ERP actually knows — what is there, and what is spoken for — and the
                    judgement is theirs. The subtraction survives only where a MACHINE has to
                    decide something (`exceedsOnHand`, the "qty exceeds available" warning), which
                    is the one place a single number is the right answer.
                  */}
                  <span className="mono" title={`${item.on_shelf ?? '—'} in stock, ${item.reserved ?? 0} reserved for other orders`}>
                    {item.on_shelf ?? onHand} {item.base_uom}
                  </span>
                  {item.reserved ? (
                    <div style={{ fontSize: 11, color: 'var(--warn)' }}>{item.reserved} reserved</div>
                  ) : null}{' '}
                  {onHand >= needQty ? (
                    <Badge tone="ok">ok</Badge>
                  ) : onHand > 0 ? (
                    <Badge tone="warn">short</Badge>
                  ) : (
                    <Badge tone="danger">nil</Badge>
                  )}
                </>
              )}
              {line.uom !== baseUom ? (
                <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>
                  need {needQty} {baseUom}
                </div>
              ) : null}
              {/* The site's copy of price and stock can lag the ERP (2026-10-26) — refresh it now. */}
              {onSyncFromErp ? (
                <div style={{ fontSize: 10.5 }}>
                  <button
                    type="button"
                    className="linkish"
                    disabled={syncingFromErp}
                    title="Pull this item's latest price and stock from the ERP"
                    onClick={onSyncFromErp}
                  >
                    {syncingFromErp ? 'syncing…' : '↻ sync from ERP'}
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            ''
          )}
        </td>

        <td>
          {line.route === 'contract' ? (
            /*
              A contract-routed line is priced one of THREE ways, and this cell used to say
              "fixed by contract" for all of them — so a line supplied from our own shelf named no
              supplier ("—") and claimed a contract that does not exist. `directPricing.ts` writes
              `IN_HOUSE_SUPPLIER` into `suppliers` precisely so the three can be told apart; this
              reads it, and each case now says where the price actually came from and what it is.
            */
            <>
              {line.suppliers.includes(IN_HOUSE_SUPPLIER) ? (
                <>
                  <span className="pill" style={{ background: 'var(--teal)', color: '#fff' }}>
                    {IN_HOUSE_SUPPLIER}
                  </span>
                  <div style={{ color: 'var(--teal)', fontSize: 11, marginTop: 3 }}>
                    our own stock @ {formatMoney({ amount: line.selected_rate ?? 0, currency: getBaseCurrency() })}/{line.uom}
                  </div>
                </>
              ) : chosenContract ? (
                <>
                  <span className="pill">{chosenContract.supplier}</span>
                  <div style={{ color: 'var(--violet)', fontSize: 11, marginTop: 3 }}>
                    contract @ {formatMoney(chosenContract.rate)}
                  </div>
                </>
              ) : (
                <>
                  <span className="pill">{line.suppliers[0] ?? '—'}</span>
                  {/* Priced without a blanket order and without our own stock — a supplier's past
                      quote (`priceFromHistory`). Named as such rather than as a contract. */}
                  <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 3 }}>
                    past price @ {formatMoney({ amount: line.selected_rate ?? 0, currency: getBaseCurrency() })}
                  </div>
                </>
              )}
            </>
          ) : unknownItem ? (
            ''
          ) : (
            <div className="chiprow">
              {line.suppliers.length ? (
                line.suppliers.map((s) => (
                  <span key={s} className="schip">
                    {supplierName(s)}
                    <b onClick={() => onRemoveSupplier(s)} title={`Remove ${supplierName(s)}`}>
                      ✕
                    </b>
                  </span>
                ))
              ) : (
                <span style={{ color: 'var(--muted)', fontSize: 11 }}>none</span>
              )}
              <Button size="sm" onClick={onOpenSuppliers}>
                ＋ suppliers
              </Button>
            </div>
          )}
        </td>

        <td>
          {/*
            No delete once a supplier has this line on a SENT RFQ (Akhil, 2026-09-15): their
            quote would land on a line that no longer exists, and nobody could tell what it was
            for. Disabled with the reason, not hidden — the purchaser should see why.
          */}
          <Button
            size="sm"
            variant="danger"
            onClick={onDelete}
            disabled={sentOnRfq !== null}
            title={sentOnRfq ? `Cannot remove — this line is with a supplier on ${sentOnRfq}` : 'Remove line'}
          >
            ✕
          </Button>
          {sentOnRfq ? (
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap' }}>on {sentOnRfq}</div>
          ) : null}
        </td>
      </tr>

      {/* The unit confirmation lives in the UOM cell above; any other note is the server's hint. */}
      {line.note && !uomConfirmed ? (
        <tr>
          <td />
          <td />
          <td colSpan={8} style={{ background: 'var(--warnbg)', color: 'var(--warn)', fontSize: 12.5 }}>
            💡 {line.note}
          </td>
        </tr>
      ) : null}
    </>
  )
}
